import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * v1.6.936 — Current-user permission gate (Asana 1215719499188980).
 *
 * Lets the panel show each person only what their role allows. Permission
 * names follow the backend's `<resource>.<action>` convention
 * (e.g. `orders.viewAny`, `products.view`, `orders.update`), so a *section*
 * is gated on the resource group ("has any `orders.*`").
 *
 * Source of truth is the authenticated user from AuthService. The service
 * reads whatever permission shape the profile/login response carries:
 *   • `permissions: string[]`            (flat names)
 *   • `permissions: [{ name }]`          (objects)
 *   • `roles: [{ permissions: [...] }]`  (via role)
 *   • `role:  { permissions: [...] }`
 *
 * Safety:
 *   • Super admins (`is_super_admin`) always pass — the owner can never be
 *     locked out and can always reach Roles & Permissions to fix a role.
 *   • If the user object carries NO permission field at all (the current
 *     profile shape), we treat permissions as UNKNOWN and fail OPEN — every
 *     item stays visible — so gating can ship inert and light up the moment
 *     the backend includes the user's permissions. An explicit empty list
 *     (`permissions: []`) is a real "no permissions" user and DOES gate.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  /** null = unknown (no permission data on the user → fail open). */
  private permsSubject = new BehaviorSubject<string[] | null>(null);
  permissions$: Observable<string[] | null> = this.permsSubject.asObservable();

  private superAdmin = false;

  constructor(private auth: AuthService) {
    this.auth.user$.subscribe(u => this.ingest(u));
  }

  private ingest(u: any): void {
    if (!u) { this.superAdmin = false; this.permsSubject.next(null); return; }
    this.superAdmin = !!u.is_super_admin;
    this.permsSubject.next(this.extractPermissions(u));
  }

  /** Pull permission names from whatever shape is present; null if none. */
  private extractPermissions(u: any): string[] | null {
    const out = new Set<string>();
    let present = false;

    const addNames = (arr: any) => {
      if (!Array.isArray(arr)) return;
      present = true;
      for (const p of arr) {
        const n = typeof p === 'string' ? p : (p?.name || p?.guard_name);
        if (typeof n === 'string' && n) out.add(n);
      }
    };

    addNames(u.permissions);
    const roles = Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : null);
    if (Array.isArray(roles)) {
      for (const r of roles) addNames(r?.permissions);
    }

    return present ? Array.from(out) : null;
  }

  /** Whether we actually know the user's permissions (else everything is allowed). */
  hasPermissionData(): boolean { return this.superAdmin || this.permsSubject.value !== null; }
  isSuperAdmin(): boolean { return this.superAdmin; }

  /** Exact permission check (e.g. 'orders.update'). */
  has(permission: string): boolean {
    if (this.superAdmin) return true;
    const p = this.permsSubject.value;
    if (p === null) return true;             // unknown → fail open
    return p.includes(permission);
  }

  hasAny(permissions: string[]): boolean {
    if (this.superAdmin) return true;
    const p = this.permsSubject.value;
    if (p === null) return true;
    return permissions.some(x => p.includes(x));
  }

  /** Resource-group check: hasGroup('orders') → any 'orders.*' (or exactly 'orders'). */
  hasGroup(prefix: string): boolean {
    if (this.superAdmin) return true;
    const p = this.permsSubject.value;
    if (p === null) return true;
    const pre = prefix.endsWith('.') ? prefix : prefix + '.';
    return p.some(x => x === prefix || x.startsWith(pre));
  }

  /**
   * Nav gate. A nav item's `permission` token is treated as a resource group
   * prefix. Untagged items are always visible.
   */
  canSee(permission?: string | null): boolean {
    if (!permission) return true;
    return this.hasGroup(permission);
  }
}
